import { Trans } from '@lingui/react/macro';
import { AlertCircle, Building2 } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { OrganizerForm } from 'modules/organizer/ui/components/organizer-form';
import { SkeletonOrganizerForm } from 'modules/organizer/ui/components/organizer-form/skeleton-organizer-form';
import { useProfileForm } from 'modules/settings/ui/hooks';
import React from 'react';
import { useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import StubSupportLink from 'ud-ui/components/stubs/components/stub-support-link/stub-support-link';
import UDText from 'ud-ui/components/text';

/**
 * Вкладка правит уже существующую организацию и никогда её не создаёт: форма без `id`
 * уходила в `upsertTournament` как создание и заводила пользователю вторую организацию.
 * Поэтому веток четыре, а не одна форма: правка, загрузка, сбой загрузки и «организации
 * нет» со ссылкой на страницу создания.
 */
function Profile() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { initialValues, isSaving, state, refetch, handleSubmit } = useProfileForm();

  return (
    <>
      {/* Заголовок жил на странице настроек над всеми вкладками; для личного кабинета он неверен. */}
      <UDText type="headline" className="mb-6 block">
        <Trans>Информация об организаторе</Trans>
      </UDText>
      <UDAsyncContent
        state={state}
        loading={<SkeletonOrganizerForm />}
        error={
          <UDEmptyState
            className="max-w-xl py-10"
            icon={<AlertCircle size={28} />}
            title={t`Не удалось загрузить данные организации`}
            subtitle={t`Проверьте соединение и попробуйте ещё раз`}
            action={
              <UDButton variant="secondary" type="button" onClick={refetch}>
                {t`Повторить`}
              </UDButton>
            }
          />
        }
        // Пустая ветка означает «бэкенд не вернул ни одной организации», а не «организации
        // точно нет»: инцидент 07.09.2026 начался ровно с такого ответа у пользователя,
        // организация у которого была. Поэтому пояснение — предупреждение, а рядом с кнопкой
        // создания стоит ссылка в поддержку: создавать вторую организацию в этом случае нельзя.
        empty={
          <UDEmptyState
            className="max-w-xl py-10"
            icon={<Building2 size={28} />}
            title={t`У вас пока нет организации`}
            subtitle={t`Если организация у вас уже есть — не создавайте новую: эта страница не должна была появиться. Напишите в поддержку, мы восстановим доступ.`}
            action={
              <div className="flex flex-col items-center gap-4">
                <StubSupportLink />
                <UDButton variant="primary" type="button" onClick={() => navigate(Pages.ORGANIZER.CREATE)}>
                  {t`Создать организацию`}
                </UDButton>
              </div>
            }
          />
        }
      >
        <OrganizerForm isLoading={isSaving} initialValues={initialValues} onSubmit={handleSubmit} />
      </UDAsyncContent>
    </>
  );
}

export default Profile;
